import React, {useState} from "react";
import {FiChevronDown, FiChevronUp} from "react-icons/fi";
import ETable, {EditIconBtn, StatusBadge, YesNoBadge} from "../components/Table";
import {fmtDateTime} from "../../../functions/formatDate";

// ─── sub-components ───────────────────────────────────────────────────────────

const DetailRow = ({label, value}) => {
    if (value == null || value === "" || value === "-") return null;
    return (
        <div style={{display: "flex", gap: "8px", alignItems: "baseline"}}>
            <span style={{fontSize: "var(--enigoo-fs-md)", color: "#94a3b8", minWidth: "110px", flexShrink: 0}}>{label}</span>
            <span style={{fontSize: "var(--enigoo-fs-lg)", color: "#374151", fontWeight: 500}}>{String(value)}</span>
        </div>
    );
};

const DetailSection = ({title, children}) => (
    <div>
        <p style={{fontSize: "var(--enigoo-fs-md)", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 5px 0"}}>{title}</p>
        <div style={{display: "flex", flexDirection: "column", gap: "3px"}}>{children}</div>
    </div>
);

const TICKET_TYPE_LABEL = {0: "Servisní karta", 1: "Běžná vstupenka", 2: "Permanentka / kredit"};
const DISCOUNT_LABEL    = {1: "Dětské", 2: "Zlevněné"};

const fmtDate = fmtDateTime;


const td = (extra = {}) => ({padding: "10px 12px", fontSize: "var(--enigoo-fs-lg)", ...extra});

const TicketRow = ({row, rowIndex}) => {
    const [expanded, setExpanded] = useState(false);
    const [hovered, setHovered] = useState(false);

    const stripeBase = rowIndex % 2 === 0 ? "#fff" : "#f8fafc";
    const bg = expanded ? "#f0f9ff" : hovered ? "#eff6ff" : stripeBase;

    return (
        <>
            <tr
                onClick={() => setExpanded(v => !v)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{background: bg, borderBottom: "1px solid #e2e8f0", cursor: "pointer"}}
            >
                <td style={td({color: "#475569", fontFamily: "monospace", whiteSpace: "nowrap"})}>{row.id}</td>
                <td style={td({fontWeight: 700, color: "#1e293b", fontFamily: "monospace"})}>{row.bc || "—"}</td>
                <td style={td({color: "#374151"})}>{row.name || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td({color: "#374151"})}>{row.tariff?.name || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td({textAlign: "center"})}><YesNoBadge value={!!row.imageUrl}/></td>
                <td style={td({color: "#475569", whiteSpace: "nowrap"})}>
                    <div>{fmtDate(row.dateFrom) || "—"}</div>
                    <div style={{color: "#94a3b8", fontSize: "var(--enigoo-fs-lg)"}}>{fmtDate(row.dateTo) || "—"}</div>
                </td>
                <td style={td({color: "#475569", textAlign: "center"})}>
                    {row.entrance == null
                        ? <span style={{color: "#cbd5e1"}}>—</span>
                        : row.entrance === 0
                            ? <span style={{fontSize: "var(--enigoo-fs-lg)", color: "#94a3b8"}}>∞</span>
                            : row.entrance
                    }
                </td>
                <td style={td()}><StatusBadge active={!row.blocked} activeLabel="Aktivní" inactiveLabel="Zablokováno"/></td>
                <td style={td()} onClick={e => e.stopPropagation()}>
                    <EditIconBtn to={"/ticket/detail/" + row.id}/>
                </td>
                <td style={td({color: "#94a3b8", textAlign: "center"})}>
                    {expanded ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>}
                </td>
            </tr>

            {expanded && (
                <tr style={{background: "#f0f9ff", borderBottom: "1px solid #e2e8f0"}}>
                    <td colSpan={10} style={{padding: "12px 16px"}}>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: "16px"}}>
                            <DetailSection title="Vstupenka">
                                <DetailRow label="ID"                value={row.id}/>
                                <DetailRow label="BC"                value={row.bc}/>
                                <DetailRow label="Jméno"             value={row.name}/>
                                <DetailRow label="Poznámka"          value={row.note}/>
                                <DetailRow label="Typ"               value={TICKET_TYPE_LABEL[row.ticketType]}/>
                                <DetailRow label="Sleva"             value={DISCOUNT_LABEL[row.type]}/>
                                <DetailRow label="Povolených vstupů" value={row.entrance === 0 ? "∞" : row.entrance}/>
                                <DetailRow label="Fotka"             value={row.imageUrl ? "Ano" : "Ne"}/>
                                <DetailRow label="Událost ID"        value={row.eventId}/>
                            </DetailSection>

                            <DetailSection title="Platnost">
                                <DetailRow label="Datum od" value={fmtDate(row.dateFrom)}/>
                                <DetailRow label="Datum do" value={fmtDate(row.dateTo)}/>
                                <DetailRow label="Čas od"   value={row.timeFrom}/>
                                <DetailRow label="Čas do"   value={row.timeTo}/>
                            </DetailSection>

                            <DetailSection title="Tarif">
                                <DetailRow label="Název" value={row.tariff?.name}/>
                                <DetailRow label="ID"    value={row.tariff?.id}/>
                            </DetailSection>

                            <DetailSection title="Stav">
                                <DetailRow label="Blokováno" value={row.blocked != null ? (row.blocked ? "Ano" : "Ne") : null}/>
                            </DetailSection>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── main ─────────────────────────────────────────────────────────────────────

const COLUMNS = [
    {header: "ID"},
    {header: "BC"},
    {header: "Jméno"},
    {header: "Tarif"},
    {header: "Fotka", center: true},
    {header: "Platnost od / do"},
    {header: "Povolených vstupů", center: true},
    {header: "Stav"},
    {header: "Edit", center: true},
    {header: ""},
];

const TicketTable = ({data, serverMode, page, pageSize, total, onPage, onPageSize}) => (
    <ETable
        columns={COLUMNS}
        data={data}
        emptyText="Žádné vstupenky"
        renderRow={(row, i) => <TicketRow key={row.id ?? i} row={row} rowIndex={i}/>}
        serverMode={serverMode}
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={onPage}
        onPageSize={onPageSize}
    />
);

export default TicketTable;
